/* eslint-disable no-unused-vars */
/* global G, PLATFORM */
import { deleteKey, getFirstItem, setKey } from 'lib/util'
import { noLoaderMiddleware } from 'platform/adapter/http/helper'

/**
 * Retrieves the users active cart from the server or creates one if the user doesn't have one.
 * Executes `G.CART, G.DONE` event afterward.
 *
 * @param {Gaia.Web.Application} app  the Platform Web Application
 * @returns {AttachmentEventListener}  an AttachmentEventListener
 */
const fn = app => async ({ detail = {} }) => {
  const { data = [] } = detail?.[G.DATA] || {}
  const adapter = app[G.ADAPTER][G.CART]
  const httpAdapter = app[G.ADAPTER][G.HTTP]
  const eventBus = app[G.ADAPTER][G.EVENTS]
  const { key: userKey } = getFirstItem(app[G.SESSION][G.STATE][G.META].refs.user)

  const { [G.API]: { type = 'cart', version = 1 } = {} } = adapter[G.STATE] || {}

  const params = {
    filter: {
      submitter: userKey, // only mine
      status: 50, // only active ones
    },
    query: { type },
  }

  try {
    const result = await httpAdapter[G.API].post(
      { url: `/api/v${version}/search`, params },
      { ...PLATFORM === 'web' && { middleware: noLoaderMiddleware } },
    )
    const activeCart = getFirstItem(result?.value)

    if (activeCart) {
      setKey(activeCart, G.DATA, adapter)

      eventBus.dispatch(eventBus.type(G.CART, G.DONE), { [G.DATA]: activeCart })
    } else {
      // user has no active cart, we need to create one
      const session = app[G.SESSION]
      const { [G.STATE]: { [G.META]: { refs: { user = null } = {} } = {} } = {} } = session || {}
      const currentUser = user ? getFirstItem(user)?.key : null

      if (!currentUser) throw new Error('Could not obtain user')

      const cartData = {
        value: {
          status: 50,
          recipientData: {},
          purchaserData: {},
          positions: [].concat(data),
          submitTimestamp: new Date().toISOString(),
        },
        refs: {
          submitter: [currentUser],
          article: [],
        },
      }

      // It is possible that we come to this point simultaneously from different origins. This is
      // because where may be multiple useCart hooks being rendered atm, each of them dispatches
      // the READ event, which will in turn trigger this event. If we don't have an active cart
      // we end up creating multiple new ones depending on how many hooks were being executed.
      // To avoid this, we set a 'semaphore' here to lock the creation of new carts while we're
      // working on this one. We will dispatch DONE event eventually in any case, so whoever is
      // listening to it will receive the newly created one
      if (!adapter[G.STATE][G.UNDO]) {
        setKey(true, G.UNDO, adapter[G.STATE])
        const created = await httpAdapter[G.API].post(
          { url: `/api/v${version}/${type}`, params: cartData },
          { ...PLATFORM === 'web' && { middleware: noLoaderMiddleware } },
        )
        const createdCart = getFirstItem(created)

        const newCart = {
          key: createdCart.id,
          _rev: createdCart.rev,
          ...cartData,
        }

        if (createdCart.ok) {
          setKey(newCart, G.DATA, adapter)
          // Resetting semaphore here
          deleteKey(G.UNDO, adapter[G.STATE])

          eventBus.dispatch(eventBus.type(G.CART, G.DONE), { [G.DATA]: newCart })
        }
      }
    }
  } catch (e) {
    console.error(e)
  }
}

export default fn
